import "shared/extensions/date";

import { CONFIGURATION_ERROR, CONSENT_ERROR, EMAIL_NOT_SHARED, EMAIL_UNAVAILABLE, GENERIC_ERROR } from "./constants";
import { ModalVariants, ModalVariantsBody } from "./interfaces";
import { ProvidersToggles, useSSO } from "../../../../../../hooks/useSSO/useSSO";
import { StatusBody, useStatusModal } from "core/hooks/useStatusModal";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AppRoutes } from "core/AppRoutes";
import AppleLogo from "shared/assets/apple-logo.png";
import { CurvedTopBanner } from "UIPalette/CurvedTopBanner/CurvedTopBanner";
import { EventNames } from "core/monitoring/types/enums";
import FacebookLogo from "shared/assets/facebook-logo.png";
import GoogleLogo from "shared/assets/google-logo.png";
import MicrosoftLogo from "shared/assets/microsoft-logo.png";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import PageTitle from "UIPalette/PageTitle/PageTitle";
import { SsoStatusBlocks } from "./Components/SsoStatusBlocks/SsoStatusBlocks";
import ViewLoading from "shared/UI/Spinners/ViewLoading";
import { capitalizeString } from "core/helpers/utils";
import { trackSSO } from "core/monitoring/Events";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FeatureToggle } from "core/auth/accessControl/FeatureToggle/FeatureToggle";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";
import { satisfies } from "semver";
import { SsoRedirectState } from "shared/core/state/ssoRedirectState";

export const SsoSettings = () => {
  const { t: tPageTitle } = useTranslation();
  const { t } = useTranslation(undefined, { keyPrefix: "accountSecurity.ssoSettings" });
  const { loading, isMobileApp, appVersion } = useMobilePlatform();
  const { Modal, setBody } = useStatusModal("sso");
  const [searchParams, setSearchParams] = useSearchParams();
  const provider = searchParams.get("provider");
  const qsError = searchParams.get("error");
  const idp = searchParams.get("idp");
  const ssoStatus = searchParams.get("status");
  const [currentProvider, setCurrentProvider] = useState(capitalizeString(provider || idp || undefined));
  const [currentModal, setCurrentModal] = useState<ModalVariants | null>(null);
  const [isRedirectLoading, setIsRedirectLoading] = useState(false);
  const [showSSO, setShowSSO] = useState<boolean>(!isMobileApp);

  const isAuthV2 = appVersion && satisfies(appVersion, process.env.REACT_APP_NEW_AUTH_VERSION!);

  const validErrorStates = useMemo(() => [GENERIC_ERROR, CONFIGURATION_ERROR, CONSENT_ERROR, EMAIL_UNAVAILABLE, EMAIL_NOT_SHARED], []);

  const errors: Record<string, string> = useMemo(
    () => ({
      [CONFIGURATION_ERROR]: t("error.body.configuration"),
      [GENERIC_ERROR]: t("error.body.generic", { provider: currentProvider }),
      [CONSENT_ERROR]: t("error.body.consent", { provider: currentProvider }),
      [EMAIL_UNAVAILABLE]: t("error.body.emailUnavailable", { provider: currentProvider }),
      [EMAIL_NOT_SHARED]: t("error.body.emailNotShared", { provider: currentProvider, interpolation: { escapeValue: false } }),
    }),
    [currentProvider, t]
  );

  const providersIcons: { [key: string]: string } = {
    Google: GoogleLogo,
    Microsoft: MicrosoftLogo,
    Apple: AppleLogo,
    Facebook: FacebookLogo,
  };

  const { unlink, setup, status } = useSSO();

  const dismissAction = useCallback(() => {
    searchParams.delete("success");
    searchParams.delete("error");
    setSearchParams((prev) => {
      const { success, ...rest } = prev as any;
      return rest;
    });
    setCurrentModal(null);
  }, [searchParams, setSearchParams]);

  const unlinkAction = useCallback(() => {
    if (currentProvider) {
      trackSSO(EventNames.SSO_UNLINK, currentProvider);
      unlink(currentProvider).catch(() => {
        setSearchParams((prev) => {
          return {
            ...prev,
            error: CONFIGURATION_ERROR,
          };
        });
      });
    }
    dismissAction();
  }, [currentProvider, unlink, dismissAction, setSearchParams]);

  const handleToggle = (targetProvider: ProvidersToggles) => {
    if (status[targetProvider].id && status[targetProvider].disabled === false) {
      setCurrentModal("unlink");
      setCurrentProvider(targetProvider);
    } else {
      trackSSO(EventNames.SSO_SETUP, targetProvider);
      handleSetupConfirmation(targetProvider);
    }
  };

  const providerEmails = useMemo(
    () => ({
      Google: status.Google.email,
      Microsoft: status.Microsoft.email,
      Apple: status.Apple.email,
      Facebook: status.Facebook.email,
    }),
    [status.Apple.email, status.Facebook.email, status.Google.email, status.Microsoft.email]
  );

  const modalVariants: Record<ModalVariants, ModalVariantsBody> = useMemo(
    () => ({
      unlink: {
        title: t("unlink.title", { provider: currentProvider }),
        body: t("unlink.body", { provider: currentProvider, email: providerEmails[currentProvider as ProvidersToggles] }),
      },
      success: {
        title: t("success.title"),
        body: t("success.body", { provider: currentProvider }),
      },
      error: {
        title: t("error.title"),
        body: errors[qsError || GENERIC_ERROR] ?? errors[GENERIC_ERROR],
      },
    }),
    [currentProvider, errors, qsError, providerEmails, t]
  );

  const modalBody: StatusBody | null = useMemo(
    () =>
      currentModal !== null
        ? {
            status: currentModal === "error" ? "error" : "success",
            data: {
              title: modalVariants[currentModal].title,
              description: modalVariants[currentModal].body,
              cta: "I understand",
              dismissCta: currentModal === "unlink" ? "Cancel" : undefined,
            },
            callback: currentModal === "unlink" ? unlinkAction : dismissAction,
            dismissCallback: dismissAction,
            cancelCallback: currentModal === "unlink" ? dismissAction : undefined,
          }
        : null,
    [currentModal, modalVariants, unlinkAction, dismissAction]
  );

  const handleSetupConfirmation = (targetProvider: ProvidersToggles) => {
    setBody({
      data: {
        title: t("setup.title"),
        description: t("setup.description", { targetProvider }),
        cta: t("setup.cta"),
      },
      status: "info",
      callback: () => {
        trackSSO(EventNames.SSO_SETUP, targetProvider);
        setup(targetProvider);
        !isAuthV2 && setIsRedirectLoading(true);
      },
      showDismiss: true,
    });
  };

  useEffect(() => {
    if (qsError !== null) {
      setCurrentModal("error");
    } else if (ssoStatus === "success") {
      setCurrentModal("success");
    }
  }, [qsError, ssoStatus, validErrorStates]);

  useEffect(() => {
    if (currentModal && ["success", "error", "unlink"].includes(currentModal)) {
      setBody(modalBody);
    } else {
      setBody(null);
    }
  }, [currentModal, setBody, modalBody]);

  useEffect(() => {
    if (isMobileApp && appVersion) {
      setShowSSO(satisfies(appVersion, process.env.REACT_APP_SSO_VERSION!));
    }
  }, [appVersion, isMobileApp]);

  const showLoading = isAuthV2 ? !!SsoRedirectState.get() : isRedirectLoading;

  if (loading && isMobileApp) {
    return null;
  }

  return (
    <FeatureToggle showFeature={showSSO} triggerUpdateModal backPath="/account/account-security">
      {showLoading && <ViewLoading type="screen" />}
      <PageTitle title={tPageTitle("pageTitle.account.accountSecurity.ssoSettings")} />
      <CurvedTopBanner>
        <PageHeader title={t("heading")} backRoute={AppRoutes.accountSecurity} />
      </CurvedTopBanner>
      <div className="mx-auto w-full md:w-[600px] space-y-8">
        <h3>{t("instruction")}</h3>
        <SsoStatusBlocks status={status} handleToggle={handleToggle} providersIcons={providersIcons} />
      </div>
      {Modal}
    </FeatureToggle>
  );
};
